import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { useLocale } from '@gov-nx/core/service';
import { today, useDateShape } from '@gov-nx/utils/common';
import { useMojeSouborySeznamContext } from '../Context';

export interface MyFilesDateRangeFormData {
	datumVytvoreniOd?: Date | null;
	datumVytvoreniDo?: Date | null;
}

interface UseMyFilesDateRangeFormResult {
	formDefinition: FormDefinition<MyFilesDateRangeFormData>;
	onSubmit: () => void;
}

export const useMyFilesDateRangeForm = (): UseMyFilesDateRangeFormResult => {
	const { code, myFilesFilters } = useMojeSouborySeznamContext();
	const { t } = useLocale(code);
	const { getDatePickerShape } = useDateShape();

	const formSchema = yup
		.object({
			datumVytvoreniOd: getDatePickerShape({
				isInvalidMessage: t('formular.validace.datum-vytvoreni-od.type'),
			})
				.max(today(), t('formular.validace.datum-vytvoreni-od.max'))
				.nullable(),
			datumVytvoreniDo: getDatePickerShape({
				isInvalidMessage: t('formular.validace.datum-vytvoreni-do.type'),
			})
				.max(today(), t('formular.validace.datum-vytvoreni-do.max'))
				.nullable()
				.min(
					yup.ref('datumVytvoreniOd'),
					t(
						'formular.validace.datum-vytvoreni-do.starsi-nez-datum-vytvoreni-od'
					)
				),
		})
		.required();

	const formMethods = useGovForm<MyFilesDateRangeFormData>({
		formSchema,
		defaultValues: {
			datumVytvoreniOd: myFilesFilters.filter.datumVytvoreniOd,
			datumVytvoreniDo: myFilesFilters.filter.datumVytvoreniDo,
		},
	});

	const formDefinition = getFormDefinition({ formMethods, formSchema });

	const onSubmit = formMethods.handleSubmit((data) =>
		myFilesFilters.submitFilters({
			datumVytvoreniOd: data.datumVytvoreniOd,
			datumVytvoreniDo: data.datumVytvoreniDo,
		})
	);

	return { formDefinition, onSubmit };
};
